import { AppIcon, AppText } from "@toothfairy/shared-ui";
import AppAssets from "@toothfairy/shared-ui/AppAssets";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import React, { useEffect, useState } from "react";
import { TouchableOpacity, View } from "react-native";
import { useLocation, useRouteMatch } from "../../Router";
import { MenuLink } from "./index";

export const MenuRow = ({
  icon,
  label,
  current,
  isSubItem,
  chevron,
  onPress,
  accessibilityLabel,
  wrapperStyle,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const RowWrapper = onPress ? TouchableOpacity : View;
  return (
    <View style={[{ width: "100%", paddingHorizontal: 2 }, wrapperStyle]}>
      <RowWrapper
        onPress={onPress}
        accessibilityLabel={accessibilityLabel}
        style={{
          flexDirection: "row",
          alignItems: "center",
          paddingVertical: 9,
          paddingHorizontal: 12,
          paddingLeft: isSubItem ? 20 : 8,
          borderRadius: 8,
          backgroundColor: current
            ? isSubItem
              ? currentTheme?.theme?.primary_soft
              : currentTheme?.theme?.primary
            : "transparent",
          cursor: "pointer",
        }}
      >
        {icon && (
          <AppIcon
            icon={icon}
            size={14}
            isActive={current && !isSubItem}
            activeColor={currentTheme?.theme?.white}
            color={
              current && isSubItem
                ? currentTheme?.theme?.primary
                : current
                ? currentTheme?.theme?.white
                : currentTheme?.theme?.primary
            }
            style={{ marginRight: 10, flex: 0 }}
            wrapperStyle={{
              marginBottom: 4,
            }}
          />
        )}
        <AppText
          accessibilityLabel={accessibilityLabel}
          color={
            current
              ? isSubItem
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.white
              : currentTheme?.theme?.font_color
          }
          style={{
            fontWeight: "600",
            fontSize: isSubItem ? 12 : 13,
          }}
          isHoverable
        >
          {label}
        </AppText>
        {chevron}
      </RowWrapper>
    </View>
  );
};

export const MenuItem = ({
  icon,
  label,
  to,
  current,
  accessibilityLabel,
  isSubItem,
  ...props
}) => {
  const _current = useRouteMatch(to)?.isExact;
  const _viewMemo = React.useMemo(() => {
    return (
      <MenuLink to={to}>
        <MenuRow
          icon={icon}
          label={label}
          current={_current || current}
          isSubItem={isSubItem}
          accessibilityLabel={accessibilityLabel}
        />
      </MenuLink>
    );
  }, [JSON.stringify(_current), JSON.stringify(current), to, label, isSubItem]);
  return _viewMemo;
};

export const ExpandableMenuItem = ({
  icon,
  label,
  items = [],
  isActive,
  accessibilityLabel,
}) => {
  const { currentTheme } = AppTheme.useTheme();
  const location = useLocation();
  const [expanded, setExpanded] = useState(!!isActive);

  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);

  const handlePress = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <View style={{ width: "100%" }}>
      <MenuRow
        icon={icon}
        label={label}
        current={false}
        accessibilityLabel={accessibilityLabel}
        onPress={handlePress}
        chevron={
          <AppIcon
            icon={
              expanded ? AppAssets.icons.arrow_up : AppAssets.icons.arrow_down
            }
            size={12}
            style={{ marginLeft: 10, flex: 0 }}
            color={
              isActive
                ? currentTheme?.theme?.primary
                : currentTheme?.theme?.font_color
            }
          />
        }
      />
      <View
        style={{
          overflow: "hidden",
          maxHeight: expanded ? items.length * 46 : 0,
          opacity: expanded ? 1 : 0,
          transitionProperty: "max-height, opacity",
          transitionDuration: "0.25s",
          transitionTimingFunction: "ease-in-out",
        }}
      >
        {items.map((item) => {
          const matches = (target) =>
            !!target &&
            (location.pathname === target ||
              location.pathname.startsWith(`${target}/`));
          const isCurrent = Array.isArray(item.matchPatterns)
            ? item.matchPatterns.some(matches)
            : matches(item.to);
          return (
            <MenuItem
              key={item.uid || item.to}
              to={item.to}
              label={item.label}
              icon={item.icon}
              isSubItem
              current={isCurrent}
              accessibilityLabel={item.accessibilityLabel || item.label}
            />
          );
        })}
      </View>
    </View>
  );
};
